"use client";

import { cn, getColors } from "@/lib/utils/utils";
import { TagSize } from "@/types/core";
import React from "react";
import { useState } from "react";

interface TextProps<T extends TagSize> extends React.PropsWithChildren {
  as?: T;
  color?: string;
  className?: string;
  center?: boolean;
  maxLength?: number;
  showMore?: boolean;
  underline?: boolean;
  link?: boolean;
  onClick?: () => void;
  showMoreText?: string;
  showLessText?: string;
  breakWords?: boolean;
  cssStyle?: React.CSSProperties;
  bdi?: boolean;
  balance?: boolean;
}

const Text: React.FC<TextProps<TagSize>> = ({
  className,
  as = "p",
  color,
  center,
  maxLength = 200,
  showMore,
  underline,
  link,
  onClick,
  showMoreText = "عرض المزيد",
  showLessText = "عرض أقل",
  breakWords,
  cssStyle,
  children,
  bdi,
  balance,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const Tag = as ?? "p";
  const underlineClass = underline ? "underline" : "";
  const linkClass = link ? "hover:underline cursor-pointer" : "";
  const centerClass = center ? "text-center" : "";
  const breakWordsClass = breakWords ? "wrap-break-word whitespace-normal" : "";
  const colorClass = color ? getColors({ color, type: "text" }) : "";
  const text = typeof children === "string" ? children : "";
  const shouldShowMore = showMore && text?.length > maxLength;
  const displayText =
    shouldShowMore && !isExpanded ? `${text.slice(0, maxLength)}...` : text;

  if (breakWords) {
    return (
      <div className="wrap-break-word whitespace-normal overflow-hidden block w-full">
        {bdi ? <bdi>{children}</bdi> : children}
      </div>
    );
  }
  return (
    <Tag
      style={cssStyle}
      className={cn(
        underlineClass,
        linkClass,
        colorClass,
        centerClass,
        breakWordsClass,
        balance ? "text-balance" : "",
        className,
      )}
      onClick={onClick}
    >
      {shouldShowMore ? (
        <span>
          {displayText}
          {!isExpanded && "... "}
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsExpanded(!isExpanded);
              }
            }}
            className={`inline-block hover:underline cursor-pointer`}
            aria-expanded={isExpanded}
          >
            {isExpanded ? showLessText : showMoreText}
          </span>
        </span>
      ) : bdi ? (
        <bdi>{children}</bdi>
      ) : (
        children
      )}
    </Tag>
  );
};

export default Text;
